import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { WalletIcon, LogOutIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { checkAuthThunk } from '@/stores/thunks/check-auth-thunk';
import { DarkModeToggle } from '@/components/dark-mode-toggle';
import { FeedbackButton } from '@/components/feedback-button';
import { Button } from './ui/button';

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(n => n[0].toUpperCase())
    .join('');
}

export const ProfileMenu: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, userProfile } = useAppSelector(state => state.vault.auth);

  useEffect(() => {
    if (isAuthenticated === null) {
      dispatch(checkAuthThunk());
    }
  }, [dispatch, isAuthenticated]);

  const initials = userProfile?.name ? getInitials(userProfile.name) : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
      variant="outline"
      size="icon"
          className="relative cursor-pointer group"
          aria-label="Profile menu"
        >
          <span className="text-xs font-semibold leading-none">{initials}</span>
    </Button>

      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {userProfile && (
          <>
            <DropdownMenuLabel className="flex flex-col gap-0.5">
              <span className="font-medium">{userProfile.name}</span>
              <span className="text-xs font-normal text-muted-foreground">{userProfile.email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuGroup>
          <div className="px-2 py-1 flex items-center gap-1">
            <DarkModeToggle />
            <FeedbackButton />
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/vaults" className="flex w-full cursor-pointer items-center">
              <WalletIcon className="mr-2 h-4 w-4" />
              My Vaults
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        {isAuthenticated === true && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <a href="/.auth/logout" className="flex w-full cursor-pointer items-center text-destructive focus:text-destructive">
                <LogOutIcon className="mr-2 h-4 w-4" />
                Sign out
              </a>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
